// src/components/Navbar/Navbar.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Navbar.css";
import Logo from "../../assets/MNGLogo.png";

const SOLID_PAGES = ["/contact", "/about-us", "/Service", "/projects", "/project-Gallery", "/privacy", "/terms"];
const SCROLL_THRESHOLD = 100;

const Navbar = () => {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(SOLID_PAGES.includes(pathname));
  const [mobileOpen, setMobileOpen] = useState(false);

  const navRef = useRef(null);
  const mobileRef = useRef(null);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (SOLID_PAGES.includes(pathname)) {
        setScrolled(true);
      } else {
        setScrolled(window.scrollY > SCROLL_THRESHOLD);
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [pathname]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mobileRef.current && !mobileRef.current.contains(e.target)) {
        if (!e.target.closest(".mobile-menu-button")) {
          setMobileOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Disable body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const closeAllMenus = () => {
    setMobileOpen(false);
  };

  return (
    <>
      <header
        ref={navRef}
        className={`navbar ${scrolled || mobileOpen ? "navbar--solid" : "navbar--transparent"}`}
        role="banner"
      >
        <div className="navbar__container container">
          {/* Brand / Logo */}
          <Link to="/" className="navbar__brand" onClick={closeAllMenus}>
            {/* NOTE: no hard pixel width/height; CSS controls sizing */}
            <img
              src={Logo}
              alt="MNG Construction LLC"
              className="navbar__logo"
              loading="eager"
              decoding="async"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="navbar__desktop" aria-label="Primary navigation">
            <div className="navbar__links">
              <Link to="/" className="navbar__link" onClick={closeAllMenus}>Home</Link>
              <Link to="/about-us" className="navbar__link" onClick={closeAllMenus}>About Us</Link>
              <Link to="/Service" className="navbar__link" onClick={closeAllMenus}>Services</Link>
              <Link to="/Projects" className="navbar__link" onClick={closeAllMenus}>Projects</Link>
              <Link to="/contact" className="navbar__link" onClick={closeAllMenus}>Contact</Link>
            </div>

      
          </nav>

          {/* Mobile Menu Button */}
          <button
            className={`mobile-menu-button ${mobileOpen ? "mobile-menu-button--open" : ""}`}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <span className="mobile-menu-button__line"></span>
            <span className="mobile-menu-button__line"></span>
            <span className="mobile-menu-button__line"></span>
          </button>
        </div>
      </header>

      {/* Mobile Navigation */}
      <div
        ref={mobileRef}
        className={`navbar__mobile ${mobileOpen ? "navbar__mobile--open" : ""}`}
        aria-modal="true"
        role="dialog"
      >
        <div className="navbar__mobile-header">
          <img src={Logo} alt="MNG Construction LLC" className="navbar__mobile-logo" />
          <button className="navbar__mobile-close" onClick={closeAllMenus} aria-label="Close menu">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <nav className="navbar__mobile-nav">
          <Link to="/" className="navbar__mobile-link" onClick={closeAllMenus}>
            <span>Home</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          <Link to="/about-us" className="navbar__mobile-link" onClick={closeAllMenus}>
            <span>About Us</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          <Link to="/Service" className="navbar__mobile-link" onClick={closeAllMenus}>
            <span>Services</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          <Link to="/Projects" className="navbar__mobile-link" onClick={closeAllMenus}>
            <span>Projects</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          <Link to="/contact" className="navbar__mobile-link" onClick={closeAllMenus}>
            <span>Contact</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        </nav>

    
      </div>

      {/* Mobile Overlay */}
      <div
        className={`navbar__mobile-overlay ${mobileOpen ? "navbar__mobile-overlay--visible" : ""}`}
        onClick={closeAllMenus}
        aria-hidden="true"
      />
    </>
  );
};

export default Navbar;
